import { Loading } from '../../subComponents/Loading';
import { version } from '../../../package.json';

export interface XHRParams {
  method: string;
  url: string;
  headers?: Record<string, string>;
  body?: any;
  /** 是否显示loading加载动画，默认为true */
  showLoading?: boolean;
  noHeader?: boolean;
}

export interface XHRResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

class XHRAdapter {
  private static defaultHeaders: Record<string, string> = {
    'Accept': 'application/json, */*',
    'Accept-Encrypt': 'Origin',
    'Content-Type': 'application/json',
    'X-Source': 'h5',
    'X-Sdk-Type': 'qcweb',
    'X-Sdk-Version': version,
  };
  private static getResponseHeaders(xhr: XMLHttpRequest): Record<string, string> {
    const headers: Record<string, string> = {};
    const headerString = xhr.getAllResponseHeaders();
    const headerPairs = headerString.trim().split(/[\r\n]+/);

    headerPairs.forEach(line => {
      const parts = line.split(': ');
      const header = parts.shift();
      const value = parts.join(': ');
      if (header) {
        headers[header.toLowerCase()] = value;
      }
    });

    return headers;
  }

  static request<T = any>(params: XHRParams): Promise<XHRResponse<T>> {
    const { method, url, headers = {}, body = null, showLoading = true, noHeader = false } = params;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open(method, url, true);
      if(noHeader) xhr.withCredentials = true;
      // 合并默认请求头
      const mergedHeaders = { ...this.defaultHeaders, ...headers };
      Object.entries(mergedHeaders).forEach(([key, value]) => {
        if(noHeader && ['X-Source', 'X-Sdk-Type', 'X-Lifecycle-Id', 'X-Device-Os', 'X-Game-Id', 'X-Game-Package', 'Content-Type', 'Accept-Encrypt'].includes(key) ) return
        xhr.setRequestHeader(key, value);
      });

      // 处理响应
      xhr.onload = () => {
        const response: XHRResponse = {
          data: JSON.parse(xhr.responseText),
          status: xhr.status,
          statusText: xhr.statusText,
          headers: this.getResponseHeaders(xhr)
        };
        if (showLoading) {
          Loading.hide();
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(response);
        } else {
          reject({
            code: xhr.status,
            message: xhr.statusText,
            data: response.data
          });
        }
      };

      // 处理网络错误
      xhr.onerror = (err) => {
        console.log('eee', err);
        if (showLoading) {
          Loading.hide();
        }
        reject({
          code: xhr.status || -2,
          message: 'Network Error',
          status: xhr.status,
          data: xhr.responseText
        });
      };
      // 发送请求
      try {
        if (showLoading) {
          Loading.show();
        }
        xhr.send(body ? JSON.stringify(body) : null);
      } catch (error) {
        reject({
          code: xhr.status || -3,
          message: 'The request sending failed.',
          data: error
        });
      }
    });
  }

  static get<T = any>(url: string, params: Omit<XHRParams, 'method' | 'url'> = {}) {
    return this.request<T>({ ...params, method: 'GET', url });
  }

  static post<T = any>(url: string, params: Omit<XHRParams, 'method' | 'url'> = {}) {
    return this.request<T>({ ...params, method: 'POST', url });
  }

};

export default XHRAdapter;
